/**
 * Minimal Markdown → structured nodes for the in-app assistant's replies.
 *
 * The model replies in Markdown (bold, italics, inline code, numbered/bulleted
 * lists, links). This turns that into a small block/inline tree that
 * AssistantWidget renders as real HTML, instead of the user seeing raw
 * `**asterisks**` and `- ` markers.
 *
 * Deliberately tiny and dependency-free (the app avoids adding deps — see
 * web/app/CLAUDE.md). It covers what the assistant actually produces, not the
 * full CommonMark spec: no nested lists, tables, blockquotes, or ATX headings.
 * App-link resolution (`app:*` → route) stays in assistantLinks.ts; this module
 * only produces the raw `href`.
 */

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; children: Inline[] }
  | { kind: 'italic'; children: Inline[] }
  | { kind: 'code'; text: string }
  | { kind: 'link'; label: string; href: string }
  // A hard line break within a paragraph (the model uses single newlines as
  // line breaks, e.g. to list a couple of items without markers).
  | { kind: 'break' };

export type Block =
  | { kind: 'paragraph'; children: Inline[] }
  | { kind: 'list'; ordered: boolean; items: Inline[][] };

// Inline spans, tried in priority order. When two match at the same position
// the earlier rule wins — so `**x**` is bold, never italic `*` + `*x*`. Each
// regex is un-anchored; parseInline picks the earliest match across all of them.
// `consume` can differ from the match length (bare URLs shed trailing
// punctuation so "…/help." doesn't 404).
type InlineRule = {
  re: RegExp;
  build: (m: RegExpExecArray) => { node: Inline; consume: number };
};

// The capture groups always fire when their pattern matches, but the app's
// `noUncheckedIndexedAccess` types `m[n]` as possibly-undefined — coalesce to ''.
// Only `*`/`**` are treated as emphasis, never `_`/`__`: the model emits
// asterisks, and honouring underscores would mangle intraword ones (an
// identifier or path like `config_version_hash` would lose its underscores and
// italicise the middle). Underscores are therefore left as literal text.
const INLINE_RULES: InlineRule[] = [
  { re: /\*\*([\s\S]+?)\*\*/, build: (m) => ({ node: { kind: 'bold', children: parseInline(m[1] ?? '') }, consume: m[0].length }) },
  { re: /`([^`]+?)`/, build: (m) => ({ node: { kind: 'code', text: m[1] ?? '' }, consume: m[0].length }) },
  { re: /\[([^\]]+)\]\(([^)\s]+)\)/, build: (m) => ({ node: { kind: 'link', label: m[1] ?? '', href: m[2] ?? '' }, consume: m[0].length }) },
  {
    re: /\bhttps?:\/\/[^\s)]+/,
    build: (m) => {
      const trimmed = m[0].replace(/[.,;:!?]+$/, '');
      return { node: { kind: 'link', label: trimmed, href: trimmed }, consume: trimmed.length };
    },
  },
  // Italic requires a non-space right after the delimiter, so a bullet "* item"
  // or a lone "5 * 3" isn't mistaken for emphasis.
  { re: /\*(\S[\s\S]*?)\*/, build: (m) => ({ node: { kind: 'italic', children: parseInline(m[1] ?? '') }, consume: m[0].length }) },
];

/** Parse a single line/run of text into inline nodes (bold, italic, code,
 * links, plain text). Recurses into bold/italic contents. */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let rest = text;
  while (rest.length > 0) {
    let best: { idx: number; rule: InlineRule; m: RegExpExecArray } | null = null;
    for (const rule of INLINE_RULES) {
      const m = rule.re.exec(rest);
      if (m && (best === null || m.index < best.idx)) best = { idx: m.index, rule, m };
    }
    if (best === null) {
      out.push({ kind: 'text', text: rest });
      break;
    }
    if (best.idx > 0) out.push({ kind: 'text', text: rest.slice(0, best.idx) });
    const { node, consume } = best.rule.build(best.m);
    out.push(node);
    rest = rest.slice(best.idx + consume);
  }
  return out;
}

const ORDERED_ITEM = /^\s*\d+[.)]\s+(.*)$/;
const UNORDERED_ITEM = /^\s*[-*+]\s+(.*)$/;

/** Parse a whole reply into block nodes: paragraphs and (flat) ordered/unordered
 * lists. Blank lines separate paragraphs; runs of `1.`/`-` lines become a list.
 *
 * Robust to how the model actually formats lists: a blank line *between* items
 * doesn't split the list (a "loose" list stays one block), and a plain line
 * right after an item is folded into it (hard-wrapped item text) rather than
 * restarting the numbering. A blank line *followed by* a non-item line does end
 * the list. Nested lists aren't modelled — an indented bullet under a number
 * becomes its own flat list. */
export function parseBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  // A blank line while inside a list: hold off deciding whether the list ended
  // until the next non-blank line — a following item continues it, other text
  // ends it.
  let blankInList = false;

  const flushPara = () => {
    if (para.length > 0) {
      // Keep single newlines as hard line breaks (the model uses them to lay out
      // a couple of lines without list markers); the old renderer preserved them
      // via `whitespace-pre-wrap`, so joining with a space would be a regression.
      const children: Inline[] = [];
      para.forEach((line, i) => {
        if (i > 0) children.push({ kind: 'break' });
        children.push(...parseInline(line));
      });
      blocks.push({ kind: 'paragraph', children });
      para = [];
    }
  };
  const flushList = () => {
    if (list !== null) {
      blocks.push({ kind: 'list', ordered: list.ordered, items: list.items.map(parseInline) });
      list = null;
    }
    blankInList = false;
  };

  for (const line of lines) {
    const ordered = ORDERED_ITEM.exec(line);
    const unordered = UNORDERED_ITEM.exec(line);
    if (ordered || unordered) {
      flushPara();
      const isOrdered = ordered !== null;
      const itemText = (ordered ?? unordered)?.[1] ?? '';
      if (list !== null && list.ordered !== isOrdered) flushList(); // type switch → new list
      if (list === null) list = { ordered: isOrdered, items: [] };
      list.items.push(itemText);
      blankInList = false;
    } else if (line.trim() === '') {
      if (list !== null) blankInList = true; // maybe a loose-list gap; decide next
      else flushPara();
    } else if (list !== null && !blankInList && /^[a-z]/.test(line.trim())) {
      // Plain line straight after an item with no blank between AND starting
      // lower-case: a hard-wrapped continuation of that item ("…plugged in / firmly
      // at both ends"). A capitalised line is treated as a new sentence/paragraph
      // instead, so a closing line like "Let me know if that helps!" isn't
      // silently swallowed into the last bullet.
      const i = list.items.length - 1;
      list.items[i] = `${list.items[i] ?? ''} ${line.trim()}`;
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return blocks;
}
